import React, { useEffect, useRef } from 'react';
import './Dialogo.css';

/**
 * Diálogo de confirmação do admin — o substituto do `window.confirm`/`window.prompt`.
 *
 * POR QUE UM COMPONENTE, e não mais um overlay local: já existem CINCO cópias de
 * `.modal-overlay` no admin (FeedbacksAdmin, PromptEditor, AwonMonitor, Home, SupportChat), cada uma
 * com seu z-index e seu comportamento de teclado. Este é o primeiro compartilhado — a próxima tela
 * que precisar de confirmação usa este em vez de virar a sexta.
 *
 * POR QUE NÃO USAR `window.prompt`: além de feio, ele não deixa explicar nada. Uma confirmação que
 * pede DATA precisa mostrar o efeito da data escolhida enquanto a pessoa digita — e é justamente
 * onde o `exited_on` engana (é o primeiro dia FORA, não o último dentro). Um prompt do browser não
 * tem onde colocar essa frase.
 *
 * O que ele resolve de acessibilidade, porque o `confirm` nativo dava de graça e é fácil perder:
 *   - `role="dialog"` + `aria-modal` + `aria-labelledby` ligando ao título;
 *   - **Esc fecha** e o clique no fundo fecha (as duas saídas que a pessoa espera);
 *   - foco vai para o primeiro campo ao abrir, e VOLTA para quem abriu ao fechar;
 *   - Enter confirma quando o foco não está num textarea;
 *   - `aria-busy` enquanto salva, e os botões travam para não haver duplo envio.
 */
function Dialogo({
    titulo,
    children,
    onCancelar,
    onConfirmar,
    rotuloConfirmar = 'Confirmar',
    rotuloCancelar = 'Cancelar',
    rotuloOcupado = 'Salvando…',
    confirmarHabilitado = true,
    ocupado = false,
    perigo = false,
    semCancelar = false,   // aviso de um botão só (o `alert` do admin)
    largo = false,         // prévia com lista: caixa mais larga, rolagem interna
}) {
    const caixaRef = useRef(null);
    const abridorRef = useRef(null);

    useEffect(() => {
        // guarda quem tinha o foco para devolver no fim — sem isso, fechar o diálogo joga o foco
        // para o começo da página e quem usa teclado perde o lugar
        abridorRef.current = document.activeElement;
        const primeiro = caixaRef.current?.querySelector(
            'input, select, textarea, button:not([data-fechar])',
        );
        primeiro?.focus();

        const aoTeclar = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCancelar();
            }
            // Enter num BOTÃO (ou link) é do próprio botão: o clique nativo já ativa, e confirmar o diálogo
            // por cima fazia "Enter em Cancelar" confirmar e fechava o diálogo ao abrir um ⓘ ou recolhível.
            const ehBotao = e.target?.tagName === 'BUTTON' || e.target?.tagName === 'A';
            if (e.key === 'Enter' && e.target?.tagName !== 'TEXTAREA' && !ehBotao && confirmarHabilitado && !ocupado) {
                onConfirmar();
            }
        };
        document.addEventListener('keydown', aoTeclar);
        return () => {
            document.removeEventListener('keydown', aoTeclar);
            if (abridorRef.current instanceof HTMLElement) abridorRef.current.focus();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmarHabilitado, ocupado]);

    return (
        <div className="dlg-fundo" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancelar(); }}>
            <div className={`dlg-caixa${largo ? ' dlg-largo' : ''}`} role="dialog" aria-modal="true" aria-labelledby="dlg-titulo"
                aria-busy={ocupado} ref={caixaRef}>
                <h3 className="dlg-titulo" id="dlg-titulo">{titulo}</h3>
                <div className="dlg-corpo">{children}</div>
                <div className="dlg-acoes">
                    {!semCancelar && (
                        <button type="button" className="dlg-btn" data-fechar onClick={onCancelar} disabled={ocupado}>
                            {rotuloCancelar}
                        </button>
                    )}
                    <button type="button"
                        className={`dlg-btn dlg-btn-ok${perigo ? ' dlg-btn-perigo' : ''}`}
                        onClick={onConfirmar}
                        disabled={ocupado || !confirmarHabilitado}>
                        {ocupado ? rotuloOcupado : rotuloConfirmar}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Dialogo;
